import {
  AlertCircle,
  Building2,
  Calendar,
  Check,
  Copy,
  ExternalLink,
  Hash,
  Mail,
  MapPin,
  Phone,
  User,
  UserCheck,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useFacilityUser from "../../../hooks/useFacilityUser";
import { Appointment } from "../../../types/appointment.definition";
import { appointmentFacilityName } from "../../../utils/appointment-facility.utils";
import { AppointmentActions } from "../AppointmentActions/AppointmentActions";
import { StatusBadge } from "../StatusBadge/StatusBadge";

interface AppointmentCardProps {
  appointment: Appointment;
  isUpdating: boolean;
  onStatusChange: (appointmentId: string, newStatus: string) => void;
}

export const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  isUpdating,
  onStatusChange,
}) => {
  const { t, i18n } = useTranslation();
  const facilityUser = useFacilityUser();
  const navigate = useNavigate();
  const [copied, setCopied] = React.useState(false);

  const locale = React.useMemo(() => {
    return i18n.language.startsWith("fr") ? "fr-FR" : "en-US";
  }, [i18n.language]);

  const handleCopyId = async () => {
    if (!appointment.id) return;

    try {
      // Check if clipboard API is available
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(appointment.id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback method using document.execCommand
        const textArea = document.createElement("textarea");
        textArea.value = appointment.id;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (successful) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } else {
          throw new Error("Fallback copy method failed");
        }
      }
    } catch (err) {
      console.error("Failed to copy appointment ID:", err);
      // Show a temporary error state
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    }
  };

  const patientName = `${appointment.patient?.firstName || ""} ${appointment.patient?.lastName || ""
    }`.trim();

  const doctorName = `${appointment.doctor?.firstName || ""} ${appointment.doctor?.lastName || ""
    }`.trim();

  const bookedByName = appointment.bookedBy
    ? `${appointment.bookedBy.firstName || ""} ${appointment.bookedBy.lastName || ""
      }`.trim()
    : "";

  // Display names if available, otherwise show IDs
  const patientDisplay =
    patientName ||
    `${t("appointment.manage.card.patientId")} ${appointment.patientId}`;
  const doctorDisplay = doctorName
    ? `Dr. ${doctorName}`
    : `${t("appointment.manage.card.doctorId")} ${appointment.doctorId}`;
  const bookedByDisplay = bookedByName
    ? t("appointment.manage.card.bookedBy", { name: bookedByName })
    : null;

  const facilityDisplay =
    appointmentFacilityName(appointment, facilityUser?.healthcareFacility) ??
    t("appointment.manage.card.facilityId", {
      id: appointment.healthcareFacilityId,
    });

  const hasAddress = appointment.location?.address;

  const handlePatientClick = () => {
    if (appointment.patientId) {
      navigate(`/patients/${appointment.patientId}`);
    }
  };

  const handleCardClick = () => {
    if (appointment.id) {
      // Appointment IDs are UUID strings, so we can use them directly
      const id = String(appointment.id).trim();
      if (id) {
        navigate(`/appointments/${id}`);
      } else {
        console.error("Invalid appointment ID format:", appointment.id);
      }
    }
  };

  return (
    <div
      className={`relative p-6 bg-white rounded-lg border border-gray-200 shadow-sm ${appointment.id ? "cursor-pointer hover:shadow-md transition-shadow" : ""
        }`}
      onClick={handleCardClick}
    >
      {/* Appointment ID Badge - Absolute positioned */}
      {appointment.id && (
        <div
          className="flex absolute top-2 right-2 z-10 items-center px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded-md transition-colors cursor-pointer hover:bg-gray-200"
          onClick={handleCopyId}
          title={t("appointment.manage.card.copyId")}
        >
          <Hash className="mr-1 w-3 h-3" />
          <span>{t("appointment.manage.card.id", { id: appointment.id })}</span>
          {copied ? (
            <Check className="ml-1 w-3 h-3 text-green-600" />
          ) : (
            <Copy className="ml-1 w-3 h-3 text-gray-400" />
          )}
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div className="flex-1">
          <div className="flex items-center mb-3 space-x-3">
            <div className="flex items-center space-x-2">
              <h3
                className={`text-lg font-semibold ${appointment.patientId
                    ? "text-primary-600 hover:text-primary-700 cursor-pointer underline"
                    : "text-gray-900"
                  }`}
                onClick={handlePatientClick}
              >
                {patientDisplay}
              </h3>
              {appointment.patientId && (
                <ExternalLink
                  className="w-4 h-4 cursor-pointer text-primary-500"
                  onClick={handlePatientClick}
                />
              )}
            </div>
            <StatusBadge status={appointment.status} />
            <span
              className={`px-3 py-1 text-xs font-medium rounded-full border ${appointment.appointmentType === "CLINIC"
                  ? "bg-blue-100 text-blue-800 border-blue-200"
                  : appointment.appointmentType === "ONLINE"
                    ? "bg-purple-100 text-purple-800 border-purple-200"
                    : "bg-gray-100 text-gray-800 border-gray-200"
                }`}
            >
              {t(`appointment.manage.serviceTypes.${appointment.appointmentType}`, { defaultValue: appointment.appointmentType })}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <div className="flex items-center text-sm text-gray-600">
              <User className="mr-2 w-4 h-4 text-gray-400" />
              {doctorDisplay}
            </div>
            {bookedByDisplay && (
              <div className="flex items-center text-sm text-gray-600">
                <UserCheck className="mr-2 w-4 h-4 text-gray-400" />
                {bookedByDisplay}
              </div>
            )}
            <div className="flex items-center text-sm text-gray-600">
              <Building2 className="mr-2 w-4 h-4 text-gray-400" />
              {facilityDisplay}
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <Calendar className="mr-2 w-4 h-4 text-gray-400" />
              {new Date(appointment.appointmentTime).toLocaleDateString(
                locale,
                { timeZone: "Africa/Dakar" }
              )}{" "}
              {t("appointment.manage.card.at")}{" "}
              {new Date(appointment.appointmentTime).toLocaleTimeString(
                locale,
                {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: "Africa/Dakar",
                }
              )}
            </div>
            {hasAddress && (
              <div className="flex items-center text-sm text-gray-600">
                <MapPin className="mr-2 w-4 h-4 text-gray-400" />
                {appointment.location?.address ||
                  t("appointment.manage.card.locationNotSpecified")}
              </div>
            )}
          </div>

          {(appointment.patient?.email || appointment.patient?.phoneNumber) && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-5 mt-3">
              {appointment.patient?.email && (
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="mr-2 w-4 h-4 text-gray-400" />
                  {appointment.patient.email}
                </div>
              )}
              {appointment.patient?.phoneNumber && (
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="mr-2 w-4 h-4 text-gray-400" />
                  {formatPhoneNumberForDisplay(appointment.patient.phoneNumber)}
                </div>
              )}
            </div>
          )}

          {appointment.reason && (
            <div className="flex items-start mt-3 text-sm text-gray-600">
              <AlertCircle className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
              {appointment.reason}
            </div>
          )}
        </div>

        <div className="flex mt-4 space-x-2 md:mt-0 min-w-[120px] justify-end">
          {appointment.id && (
            <AppointmentActions
              appointment={appointment}
              status={appointment.status}
              isUpdating={isUpdating}
              onStatusChange={onStatusChange}
              showCancel={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};
